import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, Platform } from 'react-native';

import { queryKeys } from './use-data';

/**
 * "Everything that might be in a badge or on the dashboard" — refreshed on demand, and
 * quietly in the background while a page is open.
 *
 * ── WHY A LIST, AND NOT JUST `invalidateQueries()` ──
 *
 * A bare `queryClient.invalidateQueries()` invalidates EVERY key, which on this app means
 * trial balances, general ledgers and register pages get re-run whenever someone taps
 * Refresh on the dashboard. Those are the slowest queries here and none of them feed a
 * badge. So the set is named explicitly, and the name of this constant is the promise:
 * adding a badge means adding its key here.
 *
 * ── WHY `refetchType: 'active'` ──
 *
 * Only queries with a live subscriber actually re-fetch; the rest are simply marked stale
 * and will refresh if something mounts them. That is what makes the 5-minute background
 * tick nearly free: the drawer badges are always mounted so they always refresh, while a
 * dashboard you are not looking at costs one cache flag and no request.
 */
export const LIVE_QUERY_KEYS = [
	// Work orders drive most of the drawer — overdue, unassigned, delegated, mine, pending,
	// needs-review, needs-approval — and the dashboard's stat tiles and overview. The
	// approval/review queues are `queryKeys.workOrders.list(...)` too, so they come along.
	queryKeys.workOrders.all,
	// Tasks and follow-ups: the To Do list and the Follow Up Tasks badge.
	queryKeys.tasks.all,
	queryKeys.followUps.all,
	// Exterior Improvements badge.
	queryKeys.applications.all,
	// The inbox: All mail, Assigned to me, Needs Triage — the label registry and the lists
	// share this root, so one key covers the rail counts and the drawer badges alike.
	queryKeys.messages.all,
	// The work-order billing queue (Needs Billing).
	queryKeys.workOrderBilling.all,
	// Ownership Transfers badge (pending-count) and Review Requests badge (counts). EXACT keys,
	// not the roots: the transfer root also holds the account-options picker, a ~40-request
	// paginated roster fetch that must not re-run on every tick while a picker is open.
	queryKeys.ownershipTransfers.pendingCount(),
	queryKeys.reviewRequests.counts(),
] as const;

const invalidateLive = async (queryClient: QueryClient): Promise<void> => {
	await Promise.all(LIVE_QUERY_KEYS.map((queryKey) =>
		queryClient.invalidateQueries({ queryKey: queryKey as readonly unknown[], refetchType: 'active' })));
};

/**
 * A Refresh control's handler, plus the in-flight flag to disable it with.
 *
 * `refreshing` is tracked here rather than left to each caller because `invalidateQueries`
 * resolves only once the refetches settle — a button that re-enables immediately looks like
 * it did nothing on a slow connection, which is what makes people mash it.
 */
export const useRefreshLiveData = (): { refresh: () => Promise<void>; refreshing: boolean } => {
	const queryClient = useQueryClient();
	const [refreshing, setRefreshing] = useState(false);

	const refresh = useCallback(async () => {
		setRefreshing(true);
		try {
			await invalidateLive(queryClient);
		} finally {
			setRefreshing(false);
		}
	}, [queryClient]);

	return { refresh, refreshing };
};

/** Five minutes. Long enough to be invisible, short enough that a badge is never stale. */
export const LIVE_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * The catch-up refresh on return is skipped if the last refresh was more recent than this,
 * so flicking between tabs (or app switching on iOS) does not re-fire every badge query.
 */
export const LIVE_REFRESH_CATCH_UP_MIN_MS = 30 * 1000;

/**
 * The background tick. Mounted ONCE, in the root layout.
 *
 * ── WHY NOT `refetchInterval` ON EACH QUERY ──
 *
 * Because it would have to be repeated on every hook that feeds a badge, and the one
 * somebody forgets is invisible — the badge simply goes stale and nobody files a bug about
 * a number that was never wrong, only old. One timer over one named list fails loudly
 * instead: the list is right or it is missing a key, and that is reviewable.
 *
 * ── WHY IT STOPS WHEN THE APP IS NOT VISIBLE ──
 *
 * A tab left open overnight would otherwise poll ~290 times before anyone looked at it. The
 * catch-up refresh on the way back is what actually matters, and it is done HERE: on web a
 * `visibilitychange` to 'visible', on native an `AppState` change to 'active', re-runs the
 * same refresh immediately (throttled by {@link LIVE_REFRESH_CATCH_UP_MIN_MS}).
 *
 * ── WHY NOT TANSTACK'S `refetchOnWindowFocus` ──
 *
 * It is off globally in `utils/query-client.ts`, and must stay off: turned on, every stale
 * query with a subscriber refires on focus — trial balances and registers included, the
 * very cost `LIVE_QUERY_KEYS` exists to avoid — and a refetch landing under an open form
 * re-runs its re-seed effects (the needs-info modal lost a reviewer's choice that way, see
 * `components/applications/needs-info-modal.tsx`). The catch-up here is scoped to the
 * badge keys only.
 */
export const useBackgroundRefresh = (intervalMs: number = LIVE_REFRESH_INTERVAL_MS): void => {
	const queryClient = useQueryClient();
	// Held in a ref so changing the interval does not tear down and restart the timer, which
	// would reset the clock and, at a short enough interval, mean it never fires.
	const intervalRef = useRef(intervalMs);
	intervalRef.current = intervalMs;

	useEffect(() => {
		const isVisible = (): boolean => (Platform.OS === 'web'
			? typeof document === 'undefined' || document.visibilityState === 'visible'
			: AppState.currentState === 'active');

		// Seeded with mount time: the badge queries have only just loaded.
		let lastRefreshAt = Date.now();
		const refresh = (): void => {
			lastRefreshAt = Date.now();
			void invalidateLive(queryClient);
		};

		let timer: ReturnType<typeof setInterval> | undefined;
		const startTimer = (): void => {
			if (timer !== undefined) clearInterval(timer);
			timer = setInterval(() => {
				if (!isVisible()) return;
				refresh();
			}, intervalRef.current);
		};
		startTimer();

		// A catch-up restarts the clock, so the next tick is a full interval after it rather
		// than a second burst right behind it.
		const catchUp = (): void => {
			if (Date.now() - lastRefreshAt < LIVE_REFRESH_CATCH_UP_MIN_MS) return;
			refresh();
			startTimer();
		};
		const stopTimer = (): void => {
			if (timer !== undefined) clearInterval(timer);
		};

		if (Platform.OS === 'web') {
			if (typeof document === 'undefined') return stopTimer;
			const onVisibilityChange = (): void => {
				if (document.visibilityState === 'visible') catchUp();
			};
			document.addEventListener('visibilitychange', onVisibilityChange);
			return () => {
				stopTimer();
				document.removeEventListener('visibilitychange', onVisibilityChange);
			};
		}

		const subscription = AppState.addEventListener('change', (state) => {
			if (state === 'active') catchUp();
		});
		return () => {
			stopTimer();
			subscription.remove();
		};
	}, [queryClient]);
};
